import FaceRetouchingNaturalOutlinedIcon from "@mui/icons-material/FaceRetouchingNaturalOutlined";
import MenuIcon from "@mui/icons-material/Menu";
import SendIcon from "@mui/icons-material/Send";
import StopRoundedIcon from "@mui/icons-material/StopRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import { Box, IconButton, Typography } from "@mui/material";
import { type FC, type KeyboardEvent, useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAnnieAskTarget } from "../../contexts/AnnieAskProvider";
import { useAuth } from "../../contexts/AuthProvider";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { useChosenDepartment } from "../../contexts/ChosenDepartment";
import { useDraftMode } from "../../contexts/DraftModeContext";
import { type AnnieResponseDepth, useAnnie } from "../../hooks/useAnnie";
import { useAnnieAvatar } from "../../hooks/useAnnieAvatar";
import { useAnnieMood } from "../../hooks/useAnnieMood";
import { useAnnieRecommendations } from "../../hooks/useAnnieRecommendations";
import { useAnnieUnreadReply } from "../../hooks/useAnnieUnreadReply";
import { useIsMobile } from "../../hooks/useMobile";
import { ASSISTANT_AVATAR, COMPOSER_CONTROL, FONT_SIZE, ICON_SIZE, INK } from "../../theme/tokens";
import type { AnnieSuggestion, AnnieWalkthrough } from "../../utils/annieStream";
import { isHeadNurse } from "../../utils/utilFunctions";
import { AssistantAvatar } from "../UIToolkit/AssistantAvatar/AssistantAvatar";
import { AssistantAvatarMood } from "../UIToolkit/AssistantAvatar/assistantAvatarConfig";
import { BetaBadge } from "../UIToolkit/Badges/BetaBadge";
import { ChatLauncherFab, ChatLauncherSurface } from "../UIToolkit/Chat/ChatLauncherFab";
import { InlineLevelPicker, type LevelOption } from "../UIToolkit/Inputs/InlineLevelPicker";
import { SystemValidationTextField } from "../UIToolkit/Inputs/SystemValidationTextField";
import { FloatingChatWindow } from "../UIToolkit/Popups/FloatingChatWindow/FloatingChatWindow";
import SystemButton from "../UIToolkit/SystemButton/SystemButton";
import { TOUCH_TARGET_SX } from "../UIToolkit/touchTarget";
import { AnnieMessages } from "./AnnieMessages";
import { AnnieSessionsSidebar } from "./AnnieSessionsSidebar";
import { AnnieStudioDialog } from "./AnnieStudio/AnnieStudioDialog";
import { useAnnieStudioNudge } from "./AnnieStudio/useAnnieStudioNudge";

/** Storage key for where the user parked the launcher FAB (kept per user). */
const ANNIE_LAUNCHER_DRAG_KEY = "annieLauncher";
const ANNIE_LAUNCHER_SIZE = ASSISTANT_AVATAR.SIZE.LAUNCHER;
const ANNIE_HEADER_AVATAR_SIZE = ASSISTANT_AVATAR.SIZE.HEADER;

enum AnnieStudioSource {
	Header = "header",
	Nudge = "nudge",
}

/**
 * Always controlled: Layout owns `open` for BOTH layouts so the conversation survives a viewport flip.
 * `onOpenChange` is required alongside `open` so the window can never be left unable to close.
 */
type Props = {
	/** Render the floating launcher FAB. False when Annie is opened from a menu entry (mobile, Hiring). */
	showLauncher?: boolean;
	/**
	 * Take Annie off the screen WITHOUT closing her — for an overlay that owns the viewport, i.e. a
	 * running walkthrough.
	 *
	 * Separate from `open` on purpose. The panel and the FAB are `position: fixed` at z-index 1300 and
	 * joyride's overlay is 1400, but its spotlight is a HOLE: whatever sits under it shows through, so an
	 * anchor behind the panel or the FAB is highlighted and then covered by Annie. Closing her instead
	 * would flip `open`, which also drives `sessionsEnabled` and reads as the user having dismissed her —
	 * neither is true. This suppresses the render and leaves the intent alone, so she returns by herself.
	 */
	concealed?: boolean;
	open: boolean;
	onOpenChange: (open: boolean) => void;
	/** Hoisted to the layout, which hosts the walkthrough so it survives the navigation a tour causes. */
	onStartWalkthrough?: (walkthrough: AnnieWalkthrough) => void;
};

/**
 * The Annie scheduler-assistant chat: a floating launcher + a compact
 * non-modal chat window (conversation bubbles, proposal/clarification cards,
 * a collapsible session rail, the composer). Fullscreen on mobile. Desktop
 * mounts it uncontrolled with its FAB; mobile controls `open` from the
 * head-nurse menu and hides the launcher.
 */
export const AnniePanel: FC<Props> = ({ open: isOpen, onOpenChange, onStartWalkthrough, showLauncher = true, concealed = false }) => {
	const setIsOpen = onOpenChange;
	const [isSidebarOpen, setIsSidebarOpen] = useState(false);
	const {
		items,
		statusText,
		workSteps,
		activeWorkStep,
		isStreaming,
		isLoadingSession,
		sessionId,
		sessions,
		isLoadingSessions,
		isSessionsError,
		deletingSessionId,
		send,
		chooseReadNurse,
		regenerate,
		choose,
		submitTimes,
		approve,
		reject,
		cancel,
		newSession,
		switchSession,
		deleteSession,
		saveFeedback,
	} = useAnnie({ sessionsEnabled: isOpen });
	const [draftMessage, setDraftMessage] = useState("");
	// DEFAULT: standard answers are the evidence-backed balance of useful context and guide accuracy.
	const [responseDepth, setResponseDepth] = useState<AnnieResponseDepth>("standard");
	const { isDraftMode } = useDraftMode();
	const draftModeHintId = useId();
	const { track } = useAnalytics();
	// Warm the approvals queue at app startup (this panel/FAB is always mounted) so the open screen shows rows
	// with no loading flash. Head-nurse-only, so non-head-nurses never hit the head-nurse-gated route.
	const { userInfo } = useAuth();
	const { chosenDepartment } = useChosenDepartment();
	const { data: recommendations } = useAnnieRecommendations(chosenDepartment.department_id, isHeadNurse(userInfo.role));
	const { settings: avatarSettings, visibleConfig, displayName } = useAnnieAvatar();
	const [isStudioOpen, setIsStudioOpen] = useState(false);
	const { mood, badgeCount } = useAnnieMood({
		items,
		isStreaming,
		recommendationsCount: recommendations?.length,
		departmentId: chosenDepartment.department_id,
	});
	const unreadReplyCount = useAnnieUnreadReply({ isOpen, isStreaming, isLoadingSession, items });
	const studioNudge = useAnnieStudioNudge(avatarSettings);
	const { t } = useTranslation();
	const { pendingAsk, clearPendingAsk, registerPanel } = useAnnieAskTarget();
	// One "mobile" definition for the whole feature — the same threshold that gates the mount/menu entry
	// (Layout/AdminDrawer use useIsMobile), so "opened from the menu" and "renders full-screen" always agree.
	const { isMobile } = useIsMobile();
	const closeSidebarOnMobile = () => {
		// On mobile the sessions rail is a full-screen overlay — collapse it after a pick so the conversation shows.
		if (isMobile) setIsSidebarOpen(false);
	};

	// Track open/close on the transition, not from the launcher — mobile opens come from the menu (no FAB),
	// so tracking only in handleOpen would silently drop every mobile session.
	const wasOpenRef = useRef(false);
	useEffect(() => {
		if (isOpen === wasOpenRef.current) return;
		wasOpenRef.current = isOpen;
		if (isOpen) track("annie_panel_opened");
		else track("annie_panel_closed", { while_streaming: isStreaming });
	}, [isOpen, isStreaming, track]);

	// Announce this panel as the target for "Ask Annie about this" so screens can show the affordance only
	// when there is something to receive it. Cleanup deregisters (the mobile/desktop instances swap on resize).
	useEffect(() => registerPanel(), [registerPanel]);

	// Drain a question another screen handed over: open at once, then send it as if it were typed. An answer
	// still running behind a closed panel is shown first, and the question is deliberately NOT cleared while a
	// turn is in flight — `send` would drop it — so the effect re-runs and asks it once the stream finishes.
	// The ref, not the cleared state, is what makes this exactly-once: clearing is a state update, so a
	// second panel instance (desktop↔mobile swapping in one commit) would still see the old value and send
	// the question twice. Keyed on the ask's id, so re-asking the SAME question still goes through.
	const askedIdRef = useRef<number | null>(null);
	// biome-ignore lint/correctness/useExhaustiveDependencies: setIsOpen is re-created each render (it closes over the controlled/uncontrolled choice); listing it would re-run this effect every render for no gain — the pendingAsk guard is what decides.
	useEffect(() => {
		if (pendingAsk === null) return;
		setIsOpen(true);
		if (isStreaming || isLoadingSession || askedIdRef.current === pendingAsk.id) return;
		askedIdRef.current = pendingAsk.id;
		clearPendingAsk();
		track("annie_asked_from_screen");
		// The handed-over question is asked at the depth the composer is set to — omitting it here would
		// silently answer at the hook's default instead of what the user picked.
		void send(pendingAsk.question, responseDepth);
	}, [pendingAsk, isStreaming, isLoadingSession, clearPendingAsk, send, track, responseDepth]);

	// The avatar draws the pending-approvals count, so only an avatar launcher announces it.
	const launcherAriaLabel = (): string => {
		const announcesPending = visibleConfig !== null && badgeCount > 0;
		if (unreadReplyCount > 0 && announcesPending)
			return t("annie.launcher.aria_unread_pending", { count: unreadReplyCount, pending: badgeCount, name: displayName });
		if (unreadReplyCount > 0) return t("annie.launcher.aria_unread", { count: unreadReplyCount, name: displayName });
		if (announcesPending) return t("annie.launcher.aria_pending", { count: badgeCount, name: displayName });
		return t("annie.launcher.tooltip", { name: displayName });
	};

	const handleOpen = () => {
		setIsOpen(true);
	};

	const handleClose = () => {
		setIsOpen(false);
	};

	const openStudio = (source: AnnieStudioSource) => {
		studioNudge.dismiss();
		setIsStudioOpen(true);
		track("annie_avatar_studio_opened", { source });
	};

	const canSend = !isDraftMode && !isStreaming && !isLoadingSession && draftMessage.trim() !== "";

	const handleSend = () => {
		if (!canSend) return;
		const message = draftMessage;
		setDraftMessage("");
		void send(message, responseDepth);
	};

	// The header settings menu keeps answer depth available without competing with the composer.
	const depthOptions = useMemo<LevelOption<AnnieResponseDepth>[]>(
		() => [
			{ value: "short", label: t("annie.depth.short"), hint: t("annie.depth.short.hint") },
			{ value: "standard", label: t("annie.depth.standard"), hint: t("annie.depth.standard.hint") },
			{ value: "detailed", label: t("annie.depth.detailed"), hint: t("annie.depth.detailed.hint") },
		],
		[t],
	);

	const handleResponseDepthChange = (depth: AnnieResponseDepth) => {
		setResponseDepth(depth);
		track("annie_response_depth_changed", { depth });
	};

	const handleOpeningPromptSelect = (prompt: string) => {
		setDraftMessage(prompt);
		track("annie_opening_prompt_selected");
	};

	// The approval-feed path: unlike the capability chips (which only prefill), tapping a recommendation
	// row fills the composer AND sends immediately, so Annie starts walking through the approval in one tap.
	const handleOpeningPromptSend = (prompt: string) => {
		void send(prompt, responseDepth);
	};

	// Tapping a follow-up chip asks Annie about the related topic, at the composer's current depth.
	const handleSuggestionSelect = (suggestion: AnnieSuggestion) => {
		track("annie_followup_selected");
		void send(t("annie.answer.followup", { topic: suggestion.title }), responseDepth, { guideTopicId: suggestion.id });
	};

	const handleComposerKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
		if (event.key === "Enter" && !event.shiftKey) {
			event.preventDefault();
			handleSend();
		}
	};

	const composer = (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
			{isDraftMode && (
				<Typography id={draftModeHintId} data-testid="annie-draft-mode-hint" sx={{ fontSize: FONT_SIZE.SECONDARY, color: "warning.dark" }}>
					{t("annie.draft.hint", { name: displayName })}
				</Typography>
			)}
			<Box sx={{ display: "flex", gap: 1, alignItems: isMobile ? "flex-end" : "flex-start" }}>
				<SystemValidationTextField
					fullWidth
					autoFocus
					dataTestId="annie-input"
					placeholder={t("annie.input.placeholder", { name: displayName })}
					value={draftMessage}
					onChange={(event) => setDraftMessage(event.target.value)}
					onKeyDown={handleComposerKeyDown}
					disabled={isDraftMode}
					reserveHeightForHelperText={false}
					injectedProps={{ multiline: true, maxRows: 4 }}
					formControlSx={{ flex: 1, minWidth: 0 }}
					inputSx={{
						"& .MuiOutlinedInput-root": { minWidth: 0, minHeight: isMobile ? undefined : COMPOSER_CONTROL.HEIGHT, py: 1 },
						"& .MuiInputBase-input": { minWidth: 0 },
					}}
				/>
				<Box sx={{ display: "flex", flexDirection: "column", alignItems: "stretch", flexShrink: 0, minWidth: 0 }}>
					{isStreaming ? (
						<SystemButton
							buttonVariant="secondary"
							size="small"
							onClick={cancel}
							data-testid="annie-stop-button"
							aria-label={t("annie.stop")}
							noFullWidthMobile
							touchTarget
							sx={{
								flexShrink: 0,
								width: { xs: isMobile ? "100%" : COMPOSER_CONTROL.HEIGHT, sm: isMobile ? "100%" : COMPOSER_CONTROL.HEIGHT },
								height: isMobile ? undefined : COMPOSER_CONTROL.HEIGHT,
								minWidth: isMobile ? undefined : 0,
								px: isMobile ? undefined : 0,
								py: isMobile ? undefined : 0,
							}}
						>
							<StopRoundedIcon fontSize="small" />
						</SystemButton>
					) : (
						<SystemButton
							buttonVariant="primary"
							size="small"
							disabled={!canSend}
							onClick={handleSend}
							data-testid="annie-send-button"
							aria-label={t("annie.send")}
							noFullWidthMobile
							touchTarget
							sx={{
								flexShrink: 0,
								width: { xs: isMobile ? "100%" : COMPOSER_CONTROL.HEIGHT, sm: isMobile ? "100%" : COMPOSER_CONTROL.HEIGHT },
								height: isMobile ? undefined : COMPOSER_CONTROL.HEIGHT,
								minWidth: isMobile ? undefined : 0,
								px: isMobile ? undefined : 0,
								py: isMobile ? undefined : 0,
							}}
						>
							<SendIcon fontSize="small" />
						</SystemButton>
					)}
				</Box>
			</Box>
			<Typography sx={{ fontSize: FONT_SIZE.SECONDARY, color: INK.MUTED, textAlign: "center", mt: 0.25 }}>
				{t("annie.disclaimer", { name: displayName })}
			</Typography>
		</Box>
	);

	const title = (
		<Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1, minWidth: 0 }}>
			<IconButton
				data-testid="annie-sessions-toggle"
				aria-label={t("annie.sessions.toggle")}
				aria-expanded={isSidebarOpen}
				size="small"
				onClick={() => setIsSidebarOpen((prev) => !prev)}
				sx={[TOUCH_TARGET_SX, { flexShrink: 0 }]}
			>
				<MenuIcon fontSize="small" />
			</IconButton>
			{visibleConfig !== null && (
				<AssistantAvatar config={visibleConfig} mood={mood} size={ANNIE_HEADER_AVATAR_SIZE} animated dataTestId="annie-header-avatar" />
			)}
			<Box component="span" data-testid="annie-panel-name" sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
				{displayName}
			</Box>
			<BetaBadge label={t("annie.beta.badge")} dataTestId="annie-beta-badge" />
		</Box>
	);

	return (
		<>
			{showLauncher && !concealed && (
				<ChatLauncherFab
					dataTestId="annie-launcher-button"
					ariaLabel={launcherAriaLabel()}
					tooltip={t("annie.launcher.tooltip", { name: displayName })}
					unreadCount={unreadReplyCount}
					onClick={handleOpen}
					size={ANNIE_LAUNCHER_SIZE}
					bottom={40}
					right={50}
					draggable
					dragStorageKey={ANNIE_LAUNCHER_DRAG_KEY}
					surface={visibleConfig === null ? ChatLauncherSurface.Brand : ChatLauncherSurface.Plain}
					icon={
						visibleConfig === null ? (
							<img src="/chat.svg" alt="" style={{ width: `${ANNIE_LAUNCHER_SIZE}px`, height: `${ANNIE_LAUNCHER_SIZE}px` }} />
						) : (
							// The open panel's header carries the live mood, so the launcher holds still behind it.
							<AssistantAvatar
								config={visibleConfig}
								mood={isOpen ? AssistantAvatarMood.Idle : mood}
								size={ANNIE_LAUNCHER_SIZE}
								animated={!isOpen}
								badgeCount={badgeCount}
								dataTestId="annie-launcher-avatar"
							/>
						)
					}
				/>
			)}
			<FloatingChatWindow
				open={isOpen && !concealed}
				onClose={handleClose}
				fullscreen={isMobile}
				dataTestId="annie-panel"
				ariaLabel={displayName}
				title={title}
				headerEndingContent={
					<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
						{!isMobile && (
							<InlineLevelPicker
								options={depthOptions}
								value={responseDepth}
								onChange={handleResponseDepthChange}
								ariaLabel={t("annie.depth.label")}
								ariaDescribedBy={isDraftMode ? draftModeHintId : undefined}
								disabled={isDraftMode}
								dataTestId="annie-depth-picker"
								triggerIcon={<TuneRoundedIcon sx={{ fontSize: ICON_SIZE.CONTROL }} />}
							/>
						)}
						<SystemButton
							iconButton
							buttonVariant="borderless"
							onClick={() => openStudio(AnnieStudioSource.Header)}
							tooltip={t("annie.studio.open", { name: displayName })}
							aria-label={t("annie.studio.open", { name: displayName })}
							aria-haspopup="dialog"
							touchTarget
							data-testid="annie-customize-button"
						>
							<FaceRetouchingNaturalOutlinedIcon fontSize="small" />
						</SystemButton>
					</Box>
				}
				footer={composer}
				sidebar={
					<AnnieSessionsSidebar
						sessions={sessions}
						isLoading={isLoadingSessions}
						isError={isSessionsError}
						activeSessionId={sessionId}
						deletingSessionId={deletingSessionId}
						onNewChat={() => {
							newSession();
							closeSidebarOnMobile();
						}}
						onSelect={(id) => {
							void switchSession(id);
							closeSidebarOnMobile();
						}}
						onDelete={(id) => void deleteSession(id)}
					/>
				}
				sidebarOpen={isSidebarOpen}
				onSidebarClose={() => setIsSidebarOpen(false)}
				sidebarBackAriaLabel={t("annie.sessions.back")}
			>
				<AnnieMessages
					onStartWalkthrough={onStartWalkthrough}
					items={items}
					statusText={statusText}
					workSteps={workSteps}
					activeWorkStep={activeWorkStep}
					isStreaming={isStreaming}
					isLoadingSession={isLoadingSession}
					onChoose={choose}
					onSubmitTimes={submitTimes}
					onApprove={approve}
					onReject={reject}
					onSaveFeedback={saveFeedback}
					onPromptSelect={handleOpeningPromptSelect}
					onOpeningPromptSend={handleOpeningPromptSend}
					onSuggestionSelect={handleSuggestionSelect}
					onNurseChoiceSelect={(itemId, name, option) => void chooseReadNurse(itemId, name, option, responseDepth)}
					onRegenerate={(answerItemId, conversationId) => regenerate(answerItemId, conversationId, responseDepth)}
					isOpen={isOpen}
					promptSelectionDisabled={isDraftMode}
					avatar={visibleConfig === null ? null : { config: visibleConfig, mood }}
					assistantName={displayName}
					studioNudge={studioNudge}
					onCustomizeFromNudge={() => openStudio(AnnieStudioSource.Nudge)}
				/>
			</FloatingChatWindow>
			<AnnieStudioDialog open={isStudioOpen} onClose={() => setIsStudioOpen(false)} />
		</>
	);
};
